import { LogicalNode } from "modules/record/record-producer/types/query.types";
import { LogicalGroup } from "./LogicalGroup";
import { useMemo } from "react";
import { normalizeLogicalNode, removeNodeById } from "./utils/ast.utils";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import type { ConditionPresentation } from "./ConditionRow";
import { useColumns } from "app/hooks/useColumns";
import { useTables } from "app/hooks/useTables";
import { useReferenceDotWalkMaxDepth } from "./useReferenceDotWalkMaxDepth";

interface FilterBuilderProps {
    value: LogicalNode | string | null | undefined;
    columns: any[];
    onChange: (value: any) => void;
    allColumns?: any[];
    tables?: any[];
    maxReferenceDepth?: number;
    formFields?: { name: string; label: string }[];
    flatFields?: boolean;
    compact?: boolean;
    addConditionLabel?: string;
    andLabel?: string;
    orLabel?: string;
    fieldAriaLabel?: string;
    presentation?: ConditionPresentation;
    guided?: boolean;
    allowedOperators?: string[];
    allowExists?: boolean;
}

export const FilterBuilder = ({
    value,
    columns,
    onChange,
    allColumns,
    tables,
    maxReferenceDepth,
    formFields,
    flatFields = false,
    compact = false,
    addConditionLabel,
    andLabel,
    orLabel,
    fieldAriaLabel,
    presentation,
    guided = false,
    allowedOperators,
    allowExists = true,
}: FilterBuilderProps) => {
    const columnsQuery = useColumns({ enabled: !allColumns });
    const tablesQuery = useTables({ enabled: !tables });
    const maxDepthQuery = useReferenceDotWalkMaxDepth();
    const { t } = useTranslation();
    const resolvedAllColumns = allColumns || columnsQuery.data || columns;
    const resolvedTables = tables || tablesQuery.data || [];
    const resolvedMaxReferenceDepth = maxReferenceDepth || maxDepthQuery.maxDepth;

    const logicalValue: LogicalNode | null = useMemo(() => {
        if (!value) return normalizeLogicalNode(null);
        try {
            return normalizeLogicalNode(value);
        } catch (error) {
            console.error("Failed to parse condition value", error);
            return null;
        }
    }, [value]);

    const handleChange = (node: LogicalNode | string) => {
        onChange(node);
    };

    const handleRemove = (id: string) => {
        if (!logicalValue) return;
        const updated = removeNodeById(logicalValue, id);
        handleChange(updated ? (updated as LogicalNode) : "");
    };

    if (!logicalValue) {
        return <Box role="alert" sx={{ width: "100%" }}>{t("Unable to read this condition")}</Box>;
    }

    return (
        <Box sx={{ width: "100%", minWidth: 0, overflow: "visible" }}>
            <LogicalGroup
                node={logicalValue}
                columns={columns}
                allColumns={resolvedAllColumns}
                tables={resolvedTables}
                maxReferenceDepth={resolvedMaxReferenceDepth}
                formFields={formFields}
                flatFields={flatFields}
                compact={compact}
                addConditionLabel={addConditionLabel}
                andLabel={andLabel}
                orLabel={orLabel}
                fieldAriaLabel={fieldAriaLabel}
                presentation={presentation}
                guided={guided}
                allowedOperators={allowedOperators}
                allowExists={allowExists}
                onChange={handleChange}
                onRemove={handleRemove}
            />
        </Box>
    );
};
